import { NotDetectedChip, markFixedWithUndo } from '../../components/issues/NotDetectedChip';
import { IssueLabelsField } from '../../components/issues/IssueLabelsField';
import { useState, useEffect, useRef } from 'react';
import { DEFAULT_ISSUE_TYPE, issueTypeLabel } from '@overlayqa/shared';
import { ISSUE_TYPE_PICKER_OPTIONS } from '../../lib/issueTypeOptions';
import { Link } from 'react-router-dom';
import { sanitizeUrl } from '@overlayqa/shared';
import type { Ticket, IssueSeverity, IssueStatus, IssueType, MentionableUser } from '@overlayqa/shared';
import { HugeiconsIcon } from '@hugeicons/react';
import { InformationCircleIcon } from '@hugeicons/core-free-icons';
import { toast } from 'sonner';
import { SelectDropdown } from '../../components/shared/SelectDropdown';
import { log } from '@/lib/logger';

type DetailsFields = { type: IssueType; severity: IssueSeverity; status: IssueStatus; assigneeId: string | null };
const detailFieldKeys = ['type', 'severity', 'status', 'assigneeId'] as const;

interface IssueDetailsCardProps {
  ticket: Ticket;
  teamMembers?: MentionableUser[];
  membersLoading?: boolean;
  membersError?: boolean;
  onRetryMembers?: () => void;
  /** When true, renders a compact 2-column grid (used inside the Figma layout). */
  compact?: boolean;
  /** Resolved display name of the creator (falls back to ID if not provided). */
  createdByName?: string;
  /**
   * Callback to save the fields the user CHANGED (a dirty subset; every key of
   * updateIssueSchema is optional). It used to send all three from their
   * last-synced values, which reverted a teammate's concurrent status change
   * when the user only touched severity (verify-team-concurrency.ts S2,
   * 2026-09-02; guard: TicketDetailsCard.dirtyOnlySave.test.tsx).
   */
  onSaveFields?: (data: { type?: IssueType; severity?: IssueSeverity; status?: IssueStatus; assigneeId?: string | null }, via?: 'not_detected_chip') => Promise<void>;
  /** Whether the save mutation is in flight. */
  isSaving?: boolean;
}

function CreatorLink({ ticket, name }: { ticket: Ticket; name?: string }) {
  if (!name || !ticket.createdById) {
    return <>{name || 'Unknown'}</>;
  }
  return (
    <Link
      to={`/projects/${ticket.projectId}/issues?createdBy=${ticket.createdById}`}
      className="text-primary-500 hover:underline"
    >
      {name}
    </Link>
  );
}

/**
 * Displays issue metadata: created-by, page URL, timestamps, and Figma link if present.
 * Two layout modes: sidebar (stacked) and compact (2-column grid for Figma layout).
 */
export function IssueDetailsCard({ ticket, compact, createdByName, onSaveFields, isSaving, teamMembers = [], membersLoading, membersError, onRetryMembers }: IssueDetailsCardProps) {
  log.info('[IssueDetailsCard] Rendering, compact:', { compact });

  const serverType = ticket.type || DEFAULT_ISSUE_TYPE;
  const [draft, setDraft] = useState<{
    ticketId: string;
    server: DetailsFields;
    edits: Partial<DetailsFields>;
  }>(() => ({
    ticketId: ticket.id,
    server: { type: serverType, severity: ticket.severity, status: ticket.status, assigneeId: ticket.assigneeId ?? null },
    edits: {},
  }));
  const pendingSave = useRef<{ ticketId: string; fields: Partial<DetailsFields> } | null>(null);

  useEffect(() => {
    const server = { type: serverType, severity: ticket.severity, status: ticket.status, assigneeId: ticket.assigneeId ?? null };
    if (pendingSave.current?.ticketId !== ticket.id) pendingSave.current = null;
    const pending = pendingSave.current;
    if (pending) {
      for (const key of detailFieldKeys) {
        if (pending.fields[key] === server[key]) delete pending.fields[key];
      }
    }
    setDraft((previous) => {
      if (previous.ticketId !== ticket.id) return { ticketId: ticket.id, server, edits: {} };
      const edits = { ...previous.edits };
      for (const key of detailFieldKeys) {
        // Only a changed value acknowledges this field. An unrelated refresh
        // can repeat the old value while a save of a different value is held.
        if (server[key] !== previous.server[key] && edits[key] === server[key]) delete edits[key];
      }
      return { ticketId: ticket.id, server, edits };
    });
  }, [ticket.id, serverType, ticket.severity, ticket.status, ticket.assigneeId]);

  function editField<K extends keyof DetailsFields>(key: K, value: DetailsFields[K]) {
    // useApiMutation.ts:58-64 starts the refresh without awaiting it. A saved
    // field stays pending until props acknowledge it, even after Save finishes.
    // Returning to its old value during that interval is a deliberate undo.
    const fieldIsPending = pendingSave.current?.ticketId === ticket.id
      && pendingSave.current.fields[key] !== undefined;
    setDraft((previous) => {
      const edits = { ...previous.edits };
      if (value === previous.server[key] && !fieldIsPending) delete edits[key];
      else edits[key] = value;
      return { ...previous, edits };
    });
  }

  const editType = draft.edits.type ?? serverType;
  const editSeverity = draft.edits.severity ?? ticket.severity;
  const editStatus = draft.edits.status ?? ticket.status;

  const editAssigneeId = draft.edits.assigneeId !== undefined ? draft.edits.assigneeId : (ticket.assigneeId ?? null);
  const assigneeOptions = [{ value: '', label: 'Unassigned' }, ...teamMembers.map((member) => ({ value: member.id, label: member.name }))];
  if (editAssigneeId && !teamMembers.some((member) => member.id === editAssigneeId)) assigneeOptions.push({ value: editAssigneeId, label: 'Assigned teammate' });
  const assigneeField = (
    <div>
      <span className="block text-xs font-medium text-gray-500 mb-1">Assignee</span>
      {onSaveFields ? <SelectDropdown aria-label="Assignee" value={editAssigneeId ?? ''} options={assigneeOptions} onChange={(value) => editField('assigneeId', value || null)} disabled={membersLoading || membersError} /> : <span className="text-sm text-gray-600">{assigneeOptions.find((option) => option.value === (editAssigneeId ?? ''))?.label}</span>}
      {membersLoading && <p className="text-xs text-gray-500 mt-1" role="status">Loading teammates...</p>}
      {membersError && <p className="text-xs text-gray-500 mt-1" role="alert">Couldn’t load teammates. <button type="button" className="text-primary-500 underline" onClick={onRetryMembers}><span className="text-cap-trim block">Try again</span></button></p>}
    </div>
  );

  const notDetectedChip = onSaveFields ? (
    <NotDetectedChip
      issue={ticket}
      onMarkFixed={() => {
        void markFixedWithUndo(ticket.status as IssueStatus, (status, via) => onSaveFields({ status }, via), (err, status) => {
          log.error('[IssueDetailsCard] Mark fixed status write failed', { status: (err as { status?: number })?.status, toStatus: status, err });
          toast.error(err instanceof Error && err.message ? err.message : 'Failed to save changes. Please try again.');
        });
      }}
    />
  ) : null;

  const hasFieldChanges =
    editType !== (ticket.type || DEFAULT_ISSUE_TYPE) ||
    editSeverity !== ticket.severity ||
    editStatus !== ticket.status || editAssigneeId !== (ticket.assigneeId ?? null);

  const handleSaveFields = async () => {
    if (!onSaveFields || !hasFieldChanges) return;
    const changed = {
      ...(editType !== (ticket.type || DEFAULT_ISSUE_TYPE) ? { type: editType } : {}),
      ...(editSeverity !== ticket.severity ? { severity: editSeverity } : {}),
      ...(editStatus !== ticket.status ? { status: editStatus } : {}),
      ...(editAssigneeId !== (ticket.assigneeId ?? null) ? { assigneeId: editAssigneeId } : {}),
    };
    log.info('[IssueDetailsCard] Saving field changes', changed);
    const saving = {
      ticketId: ticket.id,
      fields: {
        ...(pendingSave.current?.ticketId === ticket.id ? pendingSave.current.fields : {}),
        ...changed,
      },
    };
    pendingSave.current = saving;
    try {
      await onSaveFields(changed);
    } catch (err) {
      if (pendingSave.current === saving) {
        for (const key of detailFieldKeys) {
          if (changed[key] !== undefined) delete saving.fields[key];
        }
      }
      log.error('[IssueDetailsCard] Failed to save field changes', { err });
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to save changes. Please try again.');
    }
  };

  const figmaLabel = ticket.figmaNodeId
    ? (ticket.figmaFrameName || `Frame: ${ticket.figmaNodeId}`)
    : ticket.figmaFileKey
      ? 'Linked to entire file'
      : null;

  const figmaUrl = ticket.figmaFrameUrl
    || (ticket.figmaFileKey && ticket.figmaNodeId
      ? `https://www.figma.com/file/${ticket.figmaFileKey}?node-id=${ticket.figmaNodeId}`
      : ticket.figmaFileKey ? `https://www.figma.com/file/${ticket.figmaFileKey}` : null);

  if (compact) {
    return (
      <section id="issue-details" className="card-sm">
        <h2 className="section-label mb-3 flex items-center gap-2"><HugeiconsIcon icon={InformationCircleIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-gray-400" aria-hidden="true" />Details</h2>
        {onSaveFields && (
          <EditableFieldsRow
            editType={editType}
            editSeverity={editSeverity}
            editStatus={editStatus}
            onTypeChange={(value) => editField('type', value)}
            onSeverityChange={(value) => editField('severity', value)}
            onStatusChange={(value) => editField('status', value)}
            assigneeField={assigneeField}
            notDetectedChip={notDetectedChip}
            hasChanges={hasFieldChanges}
            isSaving={isSaving}
            onSave={handleSaveFields}
          />
        )}
        {!onSaveFields && <div className="mt-3">{assigneeField}</div>}
        <div className="mt-3"><IssueLabelsField issueId={ticket.id} labels={ticket.labels} readOnly={!onSaveFields} variant="field" /></div>
        <dl className="grid grid-cols-2 gap-4 text-sm mt-4">
          <div>
            <dt className="section-title mb-1">Created by</dt>
            <dd className="text-sm text-gray-600">
              <CreatorLink ticket={ticket} name={createdByName} />
            </dd>
          </div>
          <div>
            <dt className="section-title mb-1">Page URL</dt>
            <dd className="text-sm text-gray-600 truncate" title={ticket.pageUrl || undefined}>
              {ticket.pageUrl ? (
                <a
                  href={sanitizeUrl(ticket.pageUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-500 hover:underline"
                >
                  {ticket.pageUrl}
                </a>
              ) : (
                'N/A'
              )}
            </dd>
          </div>
          <div>
            <dt className="section-title mb-1">Created</dt>
            <dd className="text-sm text-gray-600">
              <time dateTime={new Date(ticket.createdAt).toISOString()}>{new Date(ticket.createdAt).toLocaleString()}</time>
            </dd>
          </div>
          {figmaLabel && (
            <div>
              <dt className="section-title mb-1">Figma</dt>
              <dd className="text-sm text-gray-600 truncate" title={figmaLabel}>
                {figmaUrl ? (
                  <a href={sanitizeUrl(figmaUrl)} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">
                    {figmaLabel}
                  </a>
                ) : figmaLabel}
              </dd>
            </div>
          )}
        </dl>
      </section>
    );
  }

  return (
    <section id="issue-details" className="card-sm">
      <h2 className="section-label mb-3 flex items-center gap-2"><HugeiconsIcon icon={InformationCircleIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-gray-400" aria-hidden="true" />Details</h2>
      {onSaveFields && (
        <EditableFieldsRow
          editType={editType}
          editSeverity={editSeverity}
          editStatus={editStatus}
          onTypeChange={(value) => editField('type', value)}
          onSeverityChange={(value) => editField('severity', value)}
          onStatusChange={(value) => editField('status', value)}
          assigneeField={assigneeField}
          notDetectedChip={notDetectedChip}
          hasChanges={hasFieldChanges}
          isSaving={isSaving}
          onSave={handleSaveFields}
        />
      )}
      {!onSaveFields && <div className="mt-3">{assigneeField}</div>}
        <div className="mt-3"><IssueLabelsField issueId={ticket.id} labels={ticket.labels} readOnly={!onSaveFields} variant="field" /></div>
      <dl className="space-y-3 text-sm mt-4">
        <div>
          <dt className="section-title mb-1">Page URL</dt>
          <dd className="text-sm text-gray-600 truncate" title={ticket.pageUrl || undefined}>
            {ticket.pageUrl ? (
              <a
                href={sanitizeUrl(ticket.pageUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-500 hover:underline"
              >
                {ticket.pageUrl}
              </a>
            ) : (
              'N/A'
            )}
          </dd>
        </div>
        <div>
          <dt className="section-title mb-1">Created by</dt>
          <dd className="text-sm text-gray-600">
            <CreatorLink ticket={ticket} name={createdByName} />
          </dd>
        </div>
        <div>
          <dt className="section-title mb-1">Created</dt>
          <dd className="text-sm text-gray-600">
            <time dateTime={new Date(ticket.createdAt).toISOString()}>{new Date(ticket.createdAt).toLocaleString()}</time>
          </dd>
        </div>
        {ticket.resolvedAt && (
          <div>
            <dt className="section-title mb-1">Resolved</dt>
            <dd className="text-sm text-gray-600">
              <time dateTime={new Date(ticket.resolvedAt).toISOString()}>{new Date(ticket.resolvedAt).toLocaleString()}</time>
            </dd>
          </div>
        )}
        {figmaLabel && (
          <div>
            <dt className="section-title mb-1">Figma</dt>
            <dd className="text-sm text-gray-600">
              {figmaUrl ? (
                <a href={sanitizeUrl(figmaUrl)} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">
                  {figmaLabel}
                </a>
              ) : figmaLabel}
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}

// ── EditableFieldsRow ──────────────────────────────────────────────────────

interface EditableFieldsRowProps {
  editType: IssueType;
  editSeverity: IssueSeverity;
  editStatus: IssueStatus;
  onTypeChange: (v: IssueType) => void;
  onSeverityChange: (v: IssueSeverity) => void;
  onStatusChange: (v: IssueStatus) => void;
  assigneeField: React.ReactNode;
  notDetectedChip?: React.ReactNode;
  hasChanges: boolean;
  isSaving?: boolean;
  onSave: () => void;
}

function EditableFieldsRow({
  editType,
  editSeverity,
  editStatus,
  onTypeChange,
  onSeverityChange,
  onStatusChange,
  assigneeField,
  notDetectedChip,
  hasChanges,
  isSaving,
  onSave,
}: EditableFieldsRowProps) {
  // A machine-stamped type the issue already carries (Debt / A11y / Token) stays
  // selectable and labelled so an unrelated edit never rewrites it; it is
  // offered nowhere else (2026-09-02).
  const typeOptions = ISSUE_TYPE_PICKER_OPTIONS.some((o) => o.value === editType)
    ? ISSUE_TYPE_PICKER_OPTIONS
    : [...ISSUE_TYPE_PICKER_OPTIONS, { value: editType, label: issueTypeLabel(editType) }];
  return (
    <div className="mt-4 space-y-3">
      {/* Auto-fit, never a fixed three columns: this row lives in the page's
          1-of-3 right column, whose card content measures 233 / 318 / 372 /
          532px at 1024 / 1280 / 1440 / 1920, and the SelectDropdown trigger
          truncates by design. Three fixed cells gave each value 97px at 1280,
          so Type read "Mism..." and Status "In Pro..." (board P1, 2026-09-08).
          The 9.25rem (148px) minimum is the longest picker label, "Improvement"
          at 91.6px in the trigger's 14px font, plus the trigger's 46px of
          padding + border + gap + chevron, plus ~10px slack; both numbers were
          read off e2e/verify-details-card-fields-fit.ts on 2026-09-08. The row
          then wraps to 1 / 2 / 2 / 3 columns at those four widths. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(9.25rem,1fr))] gap-3">
        <div>
          <span className="block text-xs font-medium text-gray-500 mb-1">Type</span>
          <SelectDropdown
            value={editType}
            onChange={(v) => onTypeChange(v as IssueType)}
            options={typeOptions}
            aria-label="Issue type"
          />
        </div>
        <div>
          <span className="block text-xs font-medium text-gray-500 mb-1">Severity</span>
          <SelectDropdown
            value={editSeverity}
            onChange={(v) => onSeverityChange(v as IssueSeverity)}
            options={[
              { value: 'critical', label: 'Critical' },
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' },
            ]}
            aria-label="Issue severity"
          />
        </div>
        <div>
          {notDetectedChip && <div className="mb-1 empty:hidden">{notDetectedChip}</div>}
          <span className="block text-xs font-medium text-gray-500 mb-1">Status</span>
          <SelectDropdown
            value={editStatus}
            onChange={(v) => onStatusChange(v as IssueStatus)}
            options={[
              { value: 'open', label: 'Open' },
              { value: 'in-progress', label: 'In Progress' },
              { value: 'resolved', label: 'Resolved' },
              { value: 'verified', label: 'Verified' },
              { value: 'closed', label: 'Closed' },
            ]}
            aria-label="Issue status"
          />
        </div>
        {/* Assignee is the grid's 4th cell, so at two columns it sits under
            Severity on Status's row (Emily, 2026-09-29). */}
        {assigneeField}
      </div>
      {hasChanges && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="btn-primary btn-sm"
          >
            <span className="text-cap-trim">{isSaving ? 'Saving...' : 'Save changes'}</span>
          </button>
        </div>
      )}
    </div>
  );
}

/** @deprecated Use IssueDetailsCard */
export const TicketDetailsCard = IssueDetailsCard;
